import classnames from 'classnames'
import { css } from 'emotion'
import { Filter, Product } from 'even-ts-static'
import * as React from 'react'
import { connect } from 'react-redux'

import { depositsCalculatorInputChanged, depositsCalculatorSliderChanged } from 'common/actions'
import { Mask } from 'embed/constants'

import AdvertiserDisclosure from 'embed/components/AdvertiserDisclosure'
import FormText from 'embed/components/FormText'
import { useTheme } from 'embed/hooks'

import { MultiProductState } from 'embed/reducers'
import { DEFAULT_AMOUNT } from '..'

const styles = require('./styles.scss')

const STEP_AMOUNT = 500
const MIN_AMOUNT = 0
const MAX_AMOUNT = 50000

const InputStyles = {
  field: styles.depositAmount,
  fieldError: '',
  fieldFocus: '',
  fieldInput: '',
  fieldLabel: '',
}

const constrainAmount = (amount: number) => Math.min(Math.max(amount, MIN_AMOUNT), MAX_AMOUNT)

interface DispatchProps {
  dispatchDepositInputChanged: (amount: number) => void
  dispatchDepositSliderChanged: (amount: number) => void
}

interface StoreProps {
  depositAmountIntent?: number
}

type Props = DispatchProps & StoreProps

const DepositAmountInputView: React.FC<Props> = ({
  dispatchDepositInputChanged,
  dispatchDepositSliderChanged,
  depositAmountIntent,
}) => {
  const onInputChange = React.useCallback(
    (_, value: string) => dispatchDepositInputChanged(constrainAmount(parseInt(value.replace(/\D/g, ''), 10))),
    [dispatchDepositInputChanged],
  )

  const onSliderChange = React.useCallback(
    (e: React.FormEvent<HTMLInputElement>) =>
      dispatchDepositSliderChanged(constrainAmount(parseInt(e.currentTarget.value, 10))),
    [dispatchDepositSliderChanged],
  )

  const getDepositAmount = React.useCallback(() => depositAmountIntent ?? DEFAULT_AMOUNT, [depositAmountIntent])

  const onDecrement = React.useCallback(
    () => dispatchDepositSliderChanged(constrainAmount(getDepositAmount() - STEP_AMOUNT)),
    [dispatchDepositSliderChanged, getDepositAmount],
  )

  const onIncrement = React.useCallback(
    () => dispatchDepositSliderChanged(constrainAmount(getDepositAmount() + STEP_AMOUNT)),
    [dispatchDepositSliderChanged, getDepositAmount],
  )

  const { backdropStyles, colorBorder, colorTextSecondary, fontFamilyPrimary } = useTheme()
  return (
    <React.Fragment>
      <div
        className={classnames(
          styles.title,
          css`
            font-family: ${fontFamilyPrimary};
          `,
        )}
      >
        Calculate Your Savings
      </div>
      <div
        className={classnames(
          styles.container,
          css`
            background-color: ${backdropStyles.backgroundColor};
            border-color: ${colorBorder};
          `,
        )}
      >
        <div className={styles.header}>
          <div
            className={classnames(
              styles.depositPrompt,
              css`
                font-family: ${fontFamilyPrimary};
              `,
            )}
          >
            I want to deposit
          </div>
          <FormText
            fieldName={Filter.DepositAmountIntentValue}
            mask={Mask.Currency}
            onChange={onInputChange}
            styles={InputStyles}
            type="tel"
            value={getDepositAmount().toString()}
          />
        </div>
        <div className={styles.controls}>
          <div className={classnames(styles.button, styles.minus)} onClick={onDecrement}>
            -
          </div>
          <input
            max={MAX_AMOUNT}
            min={MIN_AMOUNT}
            onChange={onSliderChange}
            step={STEP_AMOUNT}
            type="range"
            value={getDepositAmount()}
          />
          <div className={classnames(styles.button, styles.plus)} onClick={onIncrement}>
            +
          </div>
        </div>
        <div
          className={classnames(
            styles.footer,
            css`
              color: ${colorTextSecondary};
            `,
          )}
        >
          Sponsored | <AdvertiserDisclosure className={styles.advertiserDisclosure} />
        </div>
      </div>
    </React.Fragment>
  )
}

const mapStateToProps = (state: MultiProductState): StoreProps => ({
  depositAmountIntent: state.filters[Product.Deposits]?.[Filter.DepositAmountIntentValue],
})

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchDepositInputChanged: (amount: number) => dispatch(depositsCalculatorInputChanged(amount)),
  dispatchDepositSliderChanged: (amount: number) => dispatch(depositsCalculatorSliderChanged(amount)),
})

export const DepositAmountInput = connect(mapStateToProps, mapDispatchToProps)(DepositAmountInputView)
